import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { USER_ROLES, type UserRole } from "@demore/shared/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

type User = { id: string; email: string; name: string; role: string; status: string };

export function UsersPage() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const { data } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<{ items: User[] }>("/api/admin/users"),
  });
  const disable = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/users/${id}/disable`, {}),
    onSuccess: () => { toast.success("User disabled"); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Disable failed"),
  });
  const enable = useMutation({
    mutationFn: (id: string) => api.patch(`/api/admin/users/${id}`, { status: "active" }),
    onSuccess: () => { toast.success("User enabled"); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Enable failed"),
  });
  const resend = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/users/${id}/resend-invite`, {}),
    onSuccess: () => toast.success("Invite re-sent", { id: "resend-invite" }),
  });
  const updateRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: UserRole }) =>
      api.patch(`/api/admin/users/${id}`, { role }),
    onSuccess: () => { toast.success("Role updated"); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Update failed"),
  });
  const isSuperAdmin = me?.role === "super_admin";
  const canResetPasswords = isSuperAdmin || me?.role === "people_ops";
  const resetPassword = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/users/${id}/reset-password`, {}),
    onSuccess: () => {
      toast.success("Password reset email sent");
      setResetTarget(null);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Password reset failed"),
  });

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between">
        <h1 className="font-head text-2xl font-semibold">Users</h1>
        <Button onClick={() => setOpen(true)}>Invite user</Button>
      </div>
      <Card>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-admin-rule text-left"><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody>
            {data?.items.map((u) => (
              <tr key={u.id} className="border-b border-admin-rule">
                <td className="p-3 font-medium">{u.name}</td>
                <td className="p-3">{u.email}</td>
                <td className="p-3">
                  {isSuperAdmin && u.id !== me?.id ? (
                    <Select
                      value={u.role}
                      onChange={(e) => updateRole.mutate({ id: u.id, role: e.target.value as UserRole })}
                      className="h-8 w-44"
                    >
                      {USER_ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                    </Select>
                  ) : (
                    <Badge variant="outline">{u.role}</Badge>
                  )}
                </td>
                <td className="p-3"><Badge variant={u.status === "active" ? "ok" : u.status === "disabled" ? "danger" : "muted"}>{u.status}</Badge></td>
                <td className="p-3 text-right space-x-2">
                  {canResetPasswords && u.status === "active" && u.id !== me?.id && (isSuperAdmin || u.role !== "super_admin") && (
                    <Button size="sm" variant="ghost" onClick={() => setResetTarget(u)}>Reset password</Button>
                  )}
                  {u.status === "invited" && <Button size="sm" variant="ghost" onClick={() => resend.mutate(u.id)}>Resend invite</Button>}
                  {u.status === "active" && u.id !== me?.id && <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => disable.mutate(u.id)}>Disable</Button>}
                  {u.status === "disabled" && isSuperAdmin && <Button size="sm" variant="ghost" onClick={() => enable.mutate(u.id)}>Enable</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <InviteDialog open={open} onOpenChange={setOpen} onSaved={() => qc.invalidateQueries({ queryKey: ["users"] })} />
      <Dialog open={Boolean(resetTarget)} onOpenChange={(next) => { if (!next) setResetTarget(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send password reset?</DialogTitle></DialogHeader>
          <DialogBody>
            <p className="text-sm text-admin-muted">
              Send a one-hour reset link to {resetTarget?.email}.
            </p>
            <p className="mt-2 text-xs text-admin-muted">
              Their password will not change until they open the link and choose a new one.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetTarget(null)}>Cancel</Button>
            <Button
              onClick={() => resetTarget && resetPassword.mutate(resetTarget.id)}
              disabled={!resetTarget || resetPassword.isPending}
            >
              {resetPassword.isPending ? "Sending..." : "Send reset link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InviteDialog({ open, onOpenChange, onSaved }: { open: boolean; onOpenChange: (v: boolean) => void; onSaved: () => void }) {
  const [form, setForm] = useState({ name: "", email: "", role: "ae" });
  const invite = useMutation({
    mutationFn: () => api.post("/api/admin/users/invite", form),
    onSuccess: () => { toast.success("Invitation sent"); onSaved(); onOpenChange(false); },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed"),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Invite a user</DialogTitle></DialogHeader>
        <DialogBody>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
                {USER_ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
              </Select>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => invite.mutate()} disabled={!form.email || !form.name || invite.isPending}>{invite.isPending ? "Sending…" : "Send invitation"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
